import React from 'react';
import { TrendingUp, Gauge, Target, Layers } from 'lucide-react';
import { OVERVIEW_STATS, LAYER_AVERAGES, USE_CASES, STATUSES, FUNCTIONS } from '../../mock';
import { useI18n } from '../../i18n';
import { Donut, BarRow, Scatter } from '../charts';

export default function Overview() {
  const { t, tf } = useI18n();
  const s = OVERVIEW_STATS;

  const money = (n) => {
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n}`;
  };

  const kpis = [
    { icon: TrendingUp, label: t('portfolioValue'), value: money(s.totalAnnualValue), color: '#6366F1' },
    { icon: Target, label: t('totalUseCases'), value: s.totalUseCases, color: '#8B5CF6' },
    { icon: Gauge, label: t('avgAbsorption'), value: `${s.avgAbsorption}%`, color: '#EC4899' },
    { icon: Layers, label: t('avgValue'), value: `${s.avgValue}%`, color: '#10B981' },
  ];

  const statusSegments = STATUSES.map((st) => ({
    value: USE_CASES.filter((u) => u.status === st.key).length,
    color: st.color,
    label: tf(st),
  }));

  const byFunction = FUNCTIONS.map((f) => ({
    label: tf(f),
    value: USE_CASES.filter((u) => u.function === f.key).length,
  }));

  const scatterPoints = USE_CASES.map((u) => ({
    title: u.title,
    value: u.value,
    absorption: u.absorption,
    color: STATUSES.find((st) => st.key === u.status)?.color || '#6366F1',
  }));

  return (
    <div className="space-y-6 animate-fade-up">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ background: `${k.color}1A` }}>
                <Icon className="w-5 h-5" style={{ color: k.color }} />
              </div>
              <div className="font-display text-2xl font-extrabold text-slate-900">{k.value}</div>
              <div className="text-xs text-slate-500 mt-1">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status donut */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">{t('portfolioStatus')}</h3>
          <div className="flex flex-col items-center">
            <Donut segments={statusSegments} centerLabel={s.totalUseCases} centerSub={t('totalUseCases')} />
            <div className="mt-5 w-full space-y-2">
              {STATUSES.map((st) => (
                <div key={st.key} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-600">
                    <span className="w-3 h-3 rounded-sm" style={{ background: st.color }} />
                    {tf(st)}
                  </span>
                  <span className="font-semibold text-slate-800">
                    {USE_CASES.filter((u) => u.status === st.key).length}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 5-layer readiness */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:col-span-2">
          <h3 className="font-display font-bold text-slate-900 mb-5">{t('fiveLayerReadiness')}</h3>
          <div className="space-y-4">
            {LAYER_AVERAGES.map((l) => (
              <BarRow key={l.key} label={tf(l)} value={l.score} color={l.color} suffix="%" />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scatter */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:col-span-2">
          <h3 className="font-display font-bold text-slate-900">{t('valueVsAbsorption')}</h3>
          <p className="text-xs text-slate-500 mt-1 mb-3">{t('valueVsAbsorptionSub')}</p>
          <Scatter points={scatterPoints} />
        </div>

        {/* By function */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 mb-5">{t('byFunction')}</h3>
          <div className="space-y-3">
            {byFunction.map((f, i) => (
              <BarRow key={i} label={f.label} value={f.value} max={10} color="#8B5CF6" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
